/**
 * useDictee — dictée vocale via l'API navigateur (Web Speech API).
 *
 * Gratuit, aucun envoi serveur : la reconnaissance se fait dans le navigateur
 * (RGPD OK — pas de flux audio vers un tiers). Support : Chrome/Edge (bon),
 * Safari (partiel). Si indisponible, `supporte` = false et on retombe sur le
 * clavier.
 *
 * Usage :
 *   const { supporte, ecoute, demarrer, arreter } = useDictee('fr-FR', (texte) => setDesc((d) => d + texte));
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// Types minimalistes (l'API n'est pas typée par défaut dans lib.dom).
interface SpeechRecognitionLike {
  lang: string; continuous: boolean; interimResults: boolean;
  start: () => void; stop: () => void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> & { length: number } }) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useDictee(lang: string, onTexte: (texte: string) => void) {
  const [supporte, setSupporte] = useState(false);
  const [ecoute, setEcoute] = useState(false);
  const recoRef = useRef<SpeechRecognitionLike | null>(null);
  const onTexteRef = useRef(onTexte);
  onTexteRef.current = onTexte;

  useEffect(() => {
    setSupporte(getRecognitionCtor() !== null);
  }, []);

  const arreter = useCallback(() => {
    try { recoRef.current?.stop(); } catch { /* ignore */ }
    setEcoute(false);
  }, []);

  const demarrer = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    const reco = new Ctor();
    reco.lang = lang;
    reco.continuous = true;
    reco.interimResults = false;
    reco.onresult = (e) => {
      let texte = '';
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        if (r && r[0]) texte += r[0].transcript;
      }
      if (texte.trim()) onTexteRef.current(texte.endsWith(' ') ? texte : texte + ' ');
    };
    reco.onerror = () => setEcoute(false);
    reco.onend = () => setEcoute(false);
    recoRef.current = reco;
    try { reco.start(); setEcoute(true); } catch { setEcoute(false); }
  }, [lang]);

  // Arrêt propre au démontage.
  useEffect(() => () => { try { recoRef.current?.stop(); } catch { /* ignore */ } }, []);

  return { supporte, ecoute, demarrer, arreter };
}
